/**
 * The waitlist form asks for an email address and an Instagram handle, and
 * both are required — `required` on each input says so, and the browser
 * enforces it before this code ever runs.
 *
 * What is left is the handle itself. A pasted profile URL, a name with the
 * at-sign already on it and the bare name all mean the same person, so they
 * are reduced to one shape here: the notification is scannable, and one search
 * of the inbox finds someone however they wrote themselves down.
 *
 * Normalising on `change` rather than on submit is deliberate. A field holding
 * nothing but spaces satisfies `required`, and rewriting it to an empty string
 * while the browser can still see it hands the refusal back to native
 * validation instead of inventing a message of our own.
 */

const INSTAGRAM_URL = /(?:instagram\.com|instagr\.am)\/([^/?#\s]+)/i;

/**
 * Reduces whatever was typed to a bare `@handle`.
 *
 * @param {string} value
 * @returns {string} The handle as `@name`, or an empty string.
 */
const normalizeHandle = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return '';

  const url = trimmed.match(INSTAGRAM_URL);
  const handle = (url ? url[1] : trimmed).replace(/^@+/, '').replace(/\/+$/, '');

  return handle ? `@${handle}` : '';
};

class WaitlistForm extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    const form = this.querySelector('form');
    const instagram = this.querySelector('[data-waitlist-instagram]');

    if (!(form instanceof HTMLFormElement)) return;
    if (!(instagram instanceof HTMLInputElement)) return;

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const normalize = () => {
      instagram.value = normalizeHandle(instagram.value);
    };

    instagram.addEventListener('change', normalize, { signal });
    form.addEventListener('submit', normalize, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }
}

if (!customElements.get('kk-waitlist-form')) {
  customElements.define('kk-waitlist-form', WaitlistForm);
}
